import { useState } from "react";
import { EnvelopeSimple, CircleNotch, CheckCircle, BookmarkSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, startGoogleSignIn } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const COPY = {
  save: { title: "Save this to your Vault", text: "Create a free profile to keep your Saved Reels synced on every device — phone, laptop and TV." },
  vault: { title: "Your Vault, everywhere", text: "Sign in to see your Saved Reels on any device. It's free and takes seconds — no password needed." },
  account: { title: "Sign in to ReelVault", text: "Sync your Saved Reels across all your devices. Free, and no password needed." },
};

const MagicForm = ({ onSent }) => {
  const { requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestMagicLink(email.trim());
      onSent(email.trim());
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please enter a valid email address.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-3" data-testid="magic-link-form">
      <input
        data-testid="magic-link-email"
        type="email"
        required
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border border-white/10 bg-black px-4 py-3 text-sm text-white placeholder-white/25 focus:border-amber focus:outline-none transition-colors duration-300"
      />
      <button
        data-testid="magic-link-submit"
        type="submit"
        disabled={busy}
        className="flex items-center justify-center gap-2 border border-amber/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:bg-amber hover:text-black transition-colors duration-300 disabled:opacity-40"
      >
        {busy ? <CircleNotch size={15} className="animate-spin" /> : <EnvelopeSimple size={15} weight="bold" />} Email me a sign-in link
      </button>
    </form>
  );
};

export const SignInModal = () => {
  const { prompt, closeSignIn } = useAuth();
  const [sentTo, setSentTo] = useState(null);
  const copy = COPY[prompt?.reason] || COPY.account;
  const onOpenChange = (open) => {
    if (!open) {
      closeSignIn();
      setTimeout(() => setSentTo(null), 300);
    }
  };

  return (
    <Dialog open={!!prompt} onOpenChange={onOpenChange}>
      <DialogContent data-testid="signin-modal" className="max-w-md border border-white/10 bg-[#0A0A0A] p-0 text-[#EDEDED] rounded-none">
        <div className="p-7 sm:p-8">
          <span className="inline-flex h-11 w-11 items-center justify-center border border-amber/40 bg-amber/10">
            <BookmarkSimple size={22} weight="fill" className="text-amber" />
          </span>
          <DialogTitle className="mt-5 font-serif text-2xl tracking-tight text-[#EDEDED]">{sentTo ? "Check your inbox" : copy.title}</DialogTitle>
          {sentTo ? (
            <div data-testid="magic-link-sent" className="mt-3">
              <DialogDescription className="text-sm text-white/55 leading-relaxed">
                We sent a sign-in link to <span className="text-white/85">{sentTo}</span>. It works once and expires in 15 minutes.
              </DialogDescription>
              <p className="mt-5 flex items-center gap-2 font-mono text-[11px] text-emerald-300"><CheckCircle size={14} weight="fill" /> Your title will be saved as soon as you sign in.</p>
              <button data-testid="magic-link-resend" onClick={() => setSentTo(null)} className="mt-6 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 hover:text-amber transition-colors">Use a different email</button>
            </div>
          ) : (
            <>
              <DialogDescription className="mt-2 text-sm text-white/55 leading-relaxed">{copy.text}</DialogDescription>
              <button
                data-testid="google-signin-btn"
                onClick={startGoogleSignIn}
                className="mt-6 flex w-full items-center justify-center gap-3 bg-white px-5 py-3 text-sm font-medium text-black hover:bg-white/90 transition-colors duration-300"
              >
                <GoogleMark /> Continue with Google
              </button>
              <div className="my-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
                <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
              </div>
              <MagicForm onSent={setSentTo} />
              <button data-testid="signin-guest-btn" onClick={() => onOpenChange(false)} className="mt-5 w-full font-mono text-[10px] uppercase tracking-[0.15em] text-white/35 hover:text-white/70 transition-colors">
                Keep browsing as guest
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
